import {
  type FormEvent,
  type KeyboardEvent,
  type ClipboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, FileText, Lock, Mail, Mic, Send, ShieldCheck,
  Sparkles, Video,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/Input';
import { solicitarMagicLink, verificarMagicPin } from '../services/authService';
import { iniciarConPassword } from '../services/dosFactoresService';
import { SegundoFactor } from '../components/auth/SegundoFactor';
import { Analytics } from '../services/analytics';
import styles from './LoginPage.module.css';

const PIN_LENGTH = 6;

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Texto a mostrar cuando falla la solicitud del código. Las Cloud Functions callable
 * traen el mensaje del servidor en `message`; solo se cae al genérico cuando no hay
 * mensaje o cuando es el «internal» que Firebase pone ante un fallo no controlado.
 */
function mensajeDeEnvio(err: unknown): string {
  const msg = err instanceof Error ? err.message.trim() : '';
  if (!msg || /^internal$/i.test(msg)) {
    return 'No se pudo enviar el codigo. Intente nuevamente.';
  }
  return msg;
}

type AuthTab = 'password' | 'magiclink';
type MagicPhase = 'email' | 'pin';

export default function LoginPage() {
  const { user, loading: authLoading, role } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<AuthTab>('password');
  // Desafío del segundo factor. Mientras exista, el primer factor ya se superó
  // pero NO hay sesión: la tarjeta muestra el panel del código en vez de las
  // pestañas. Es lo que impide que un camino de ingreso entregue acceso solo.
  const [desafio, setDesafio] = useState<
    { id: string; modo: 'codigo' | 'enrolar'; correo: string } | null
  >(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Magic link PIN state
  const [magicPhase, setMagicPhase] = useState<MagicPhase>('email');
  const [magicEmail, setMagicEmail] = useState('');
  const [pin, setPin] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [shake, setShake] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!authLoading && user) {
      // El gestor de notificaciones entra directo a su módulo
      // El visor solo tiene los tableros de televisor: entrar a /salas le
      // mostraría una pantalla vacía por permisos.
      const destino = role === 'notificador' ? '/notificaciones'
        : role === 'visor' ? '/tv'
        : '/salas';
      navigate(destino, { replace: true });
    }
  }, [user, authLoading, role, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const validateEmail = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value && !isValidEmail(value)) {
        setEmailError('Formato de correo invalido');
      } else {
        setEmailError('');
      }
    }, 500);
  }, []);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setError('');
    validateEmail(value);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setError('');
  };

  // ─── Login con email/password ───────────────────────────

  const canSubmitPassword = email.trim() !== '' && password.trim() !== '' && !submitting;

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmitPassword) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isValidEmail(email)) {
      setEmailError('Formato de correo invalido');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // No se llama a `login`: verificar la contraseña en el navegador ya abriría
      // sesión y el segundo factor sería una cortina que se puede rodear.
      const r = await iniciarConPassword(email, password);
      // Persona exenta: el servicio ya abrió la sesión. Sin este caso, el
      // usuario quedaba mirando la pantalla del código mientras el servidor le
      // entregaba el acceso una y otra vez.
      if (r.entroDirecto) {
        navigate('/salas', { replace: true });
        return;
      }
      setDesafio({
        id: r.desafioId!,
        modo: r.requiereEnrolamiento ? 'enrolar' : 'codigo',
        correo: email.trim(),
      });
    } catch (err: unknown) {
      console.error('[Login] Error:', err);
      const msg = err instanceof Error ? err.message : 'Error al iniciar sesion';
      if (msg.includes('desactivada')) {
        setError(msg);
      } else if (msg.includes('verificar')) {
        setError(msg);
      } else {
        setError('Credenciales invalidas. Verifique su correo y contrasena.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Magic Link: send PIN ──────────────────────────────

  const canSubmitMagicLink = email.trim() !== '' && !submitting;

  const handleMagicLinkSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmitMagicLink) return;
    if (!isValidEmail(email)) {
      setEmailError('Formato de correo invalido');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await solicitarMagicLink(email);
      setMagicEmail(email);
      setMagicPhase('pin');
      setCooldown(60);
      setTimeout(() => pinRefs.current[0]?.focus(), 200);
    } catch (err: unknown) {
      // El servidor ahora distingue los motivos (correo no registrado, cuenta
      // desactivada, fallo de envío) y manda el texto ya redactado. Antes todo caía en
      // «Error al enviar el codigo» o, peor, se pasaba a la pantalla del código aunque
      // nunca se hubiera generado ninguno.
      setError(mensajeDeEnvio(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Magic Link: verify PIN ────────────────────────────

  const fullPin = pin.join('');

  const verifyingRef = useRef(false);

  const handleAutoVerify = useCallback(async (currentPin: string) => {
    if (currentPin.length !== PIN_LENGTH || verifyingRef.current) return;

    verifyingRef.current = true;
    setVerifying(true);
    setPinError('');

    try {
      const r = await verificarMagicPin(magicEmail, currentPin);
      Analytics.loginSuccess('magic_pin');
      if (r.entroDirecto) {
        navigate('/salas', { replace: true });
        return;
      }
      // El PIN es solo el primer factor: la sesión la emite el segundo paso.
      setDesafio({
        id: r.desafioId!,
        modo: r.requiereEnrolamiento ? 'enrolar' : 'codigo',
        correo: magicEmail,
      });
    } catch (err: unknown) {
      // Firebase Functions devuelve FunctionsError con .code y .message
      const e = err as { code?: string; message?: string };
      const code = e.code ?? '';
      const msg = e.message || 'Codigo incorrecto';

      // Errores de configuracion del servidor — no es culpa del usuario,
      // no limpiar el PIN ni hacer shake
      const isServerError =
        code === 'functions/internal' ||
        code === 'functions/failed-precondition' ||
        code === 'internal' ||
        code === 'failed-precondition' ||
        msg.toLowerCase().includes('iam') ||
        msg.toLowerCase().includes('token de sesion');

      if (isServerError) {
        setPinError(msg);
      } else {
        setPinError(msg);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setPin(Array(PIN_LENGTH).fill(''));
        setTimeout(() => pinRefs.current[0]?.focus(), 100);
      }
      // Log para diagnostico (visible en consola del navegador)
      console.error('[verificarMagicPin] error:', { code, message: msg, raw: err });
      verifyingRef.current = false;
    } finally {
      setVerifying(false);
    }
  }, [magicEmail, navigate]);

  useEffect(() => {
    if (fullPin.length === PIN_LENGTH) {
      handleAutoVerify(fullPin);
    }
  }, [fullPin, handleAutoVerify]);

  const handlePinChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setPinError('');
    if (value && index < PIN_LENGTH - 1) {
      pinRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const newPin = [...pin];
      newPin[index - 1] = '';
      setPin(newPin);
      pinRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) pinRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) pinRefs.current[index + 1]?.focus();
  };

  const handlePinPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    const newPin = Array(PIN_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { newPin[i] = char; });
    setPin(newPin);
    pinRefs.current[Math.min(pasted.length, PIN_LENGTH - 1)]?.focus();
  };

  const handleResend = async () => {
    if (cooldown > 0 || submitting) return;
    setSubmitting(true);
    try {
      await solicitarMagicLink(magicEmail);
      setCooldown(60);
      setPin(Array(PIN_LENGTH).fill(''));
      setPinError('');
    } catch {
      setPinError('Error al reenviar el codigo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToEmail = () => {
    setMagicPhase('email');
    setPin(Array(PIN_LENGTH).fill(''));
    setPinError('');
    setError('');
  };

  if (authLoading) return null;
  if (user) return null;

  return (
    <div className={styles.container}>
      {/* Left - Form */}
      <div className={styles.leftPanel}>
        <div className={styles.formWrapper}>
          <div className={styles.brand}>
            <img className={styles.brandLogo} src="/minsalud-logo.png" alt="MINSAlud — Portal del Paciente" />
          </div>

          <div className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            <span>Acceso seguro · Portal de Teleconsulta</span>
          </div>

          <h1 className={styles.title}>
            Bienvenido<br />
            <span className={styles.titleAccent}>de nuevo.</span>
          </h1>

          <p className={styles.subtitle}>
            Ingrese con su cuenta institucional. El portal se adapta a su
            rol: consulta, supervisión, administración o notificaciones.
          </p>

          {/* Segundo factor: reemplaza las pestañas mientras el desafío esté
              vivo. El primer factor ya se superó, pero todavía no hay sesión. */}
          {desafio ? (
            <SegundoFactor
              desafioId={desafio.id}
              modo={desafio.modo}
              correo={desafio.correo}
              onListo={() => navigate('/salas', { replace: true })}
              onCancelar={() => {
                setDesafio(null);
                setPin(Array(PIN_LENGTH).fill(''));
                setMagicPhase('email');
                setError('');
              }}
            />
          ) : (
          <>
          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'password' ? styles.tabActive : ''}`}
              onClick={() => { setActiveTab('password'); setError(''); setMagicPhase('email'); }}
            >
              <Lock size={14} />
              Contrasena
            </button>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'magiclink' ? styles.tabActive : ''}`}
              onClick={() => { setActiveTab('magiclink'); setError(''); }}
            >
              <Send size={14} />
              Codigo de acceso
            </button>
          </div>

          {/* Tab 1: Email + Password */}
          {activeTab === 'password' && (
            <form className={styles.form} onSubmit={handlePasswordSubmit}>
              <Input
                label="Correo institucional"
                type="email"
                placeholder="correo@ejemplo.com"
                leftIcon={<Mail size={16} />}
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                error={emailError || undefined}
                disabled={submitting}
                autoComplete="email"
                required
              />
              <Input
                label="Contrasena"
                type="password"
                placeholder="Ingrese su contrasena"
                leftIcon={<Lock size={16} />}
                showPasswordToggle
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                disabled={submitting}
                autoComplete="current-password"
                required
              />
              <div className={styles.formRow}>
                <label className={styles.checkLabel}>
                  <input type="checkbox" defaultChecked className={styles.checkbox} />
                  Mantener sesion
                </label>
                <Link to="/reset-password" className={styles.forgotLink}>
                  ¿Olvidaste tu contrasena?
                </Link>
              </div>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={!canSubmitPassword || submitting}
              >
                {submitting ? 'Verificando...' : (
                  <>
                    Ingresar al portal
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
              {error && (
                <div className={styles.errorAlert} role="alert">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}
            </form>
          )}

          {/* Tab 2: Magic Link — Email phase */}
          {activeTab === 'magiclink' && magicPhase === 'email' && (
            <form className={styles.form} onSubmit={handleMagicLinkSubmit}>
              <Input
                label="Correo institucional"
                type="email"
                placeholder="correo@ejemplo.com"
                leftIcon={<Mail size={16} />}
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                error={emailError || undefined}
                disabled={submitting}
                autoComplete="email"
                required
              />
              <p className={styles.magicLinkHint}>
                Recibira un codigo de 6 digitos en su correo. Sin necesidad de contrasena.
              </p>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={!canSubmitMagicLink || submitting}
              >
                {submitting ? 'Enviando...' : (
                  <>
                    Enviar codigo de acceso
                    <Send size={16} />
                  </>
                )}
              </button>
              {error && (
                <div className={styles.errorAlert} role="alert">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}
            </form>
          )}

          {/* Tab 2: Magic Link — PIN phase */}
          {activeTab === 'magiclink' && magicPhase === 'pin' && (
            <div className={styles.pinSection}>
              <div className={styles.pinIcon}>
                <ShieldCheck size={36} />
              </div>
              <p className={styles.pinTitle}>Ingrese el codigo</p>
              <p className={styles.pinSubtitle}>
                Enviamos un codigo de 6 digitos a<br />
                <strong>{magicEmail}</strong>
              </p>

              <div className={`${styles.pinContainer} ${shake ? styles.pinShake : ''}`}>
                {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                  <input
                    key={i}
                    ref={(el) => { pinRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={pin[i]}
                    onChange={(e) => handlePinChange(i, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(i, e)}
                    onPaste={i === 0 ? handlePinPaste : undefined}
                    onFocus={(e) => e.target.select()}
                    className={`${styles.pinInput} ${pin[i] ? styles.pinFilled : ''} ${pinError ? styles.pinErrorInput : ''}`}
                    disabled={verifying}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              {verifying && (
                <p className={styles.verifyingText}>Verificando...</p>
              )}

              {pinError && (
                <div className={styles.errorAlert}>
                  <AlertCircle size={18} />
                  <span>{pinError}</span>
                </div>
              )}

              <div className={styles.resendRow}>
                <span className={styles.resendLabel}>¿No recibio el codigo?</span>
                <button
                  type="button"
                  className={styles.resendBtn}
                  onClick={handleResend}
                  disabled={cooldown > 0 || submitting}
                >
                  {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar codigo'}
                </button>
              </div>

              <button type="button" className={styles.backBtn} onClick={handleBackToEmail}>
                Usar otro correo
              </button>
            </div>
          )}

          </>
          )}

          <div className={styles.footNote}>
            ¿Nuevo en el sistema?{' '}
            <Link to="/registro" className={styles.footLink}>Solicita acceso</Link>
          </div>
        </div>
      </div>

      {/* Right — La consulta que se documenta sola */}
      <aside className={styles.rightPanel} aria-hidden="true">
        <div className={styles.auroraA} />
        <div className={styles.auroraB} />
        <div className={styles.rightContent}>
          <div className={styles.showEyebrow}>
            <span className={styles.liveDot} />
            Teleconsulta · Ministerio de Salud · Panamá
          </div>

          <h2 className={styles.showTitle}>
            La consulta que <em>se documenta sola</em>.
          </h2>

          <p className={styles.showSub}>
            Mientras usted atiende, el asistente clínico transcribe la
            conversación y redacta la nota SOAPE en tiempo real. Al colgar,
            la nota está lista para firmar.
          </p>

          {/* Nota SOAPE en miniatura: la IA "escribiendo" en vivo */}
          <div className={styles.noteCard}>
            <div className={styles.noteHead}>
              <span className={styles.noteChip}>
                <Sparkles size={11} /> Asistente clínico
              </span>
              <span className={styles.noteLive}>
                <span className={styles.liveDot} />
                EN VIVO
              </span>
            </div>
            <div className={styles.noteSection}>Subjetivo</div>
            <div className={styles.noteLine} style={{ width: '92%' }} />
            <div className={styles.noteLine} style={{ width: '76%' }} />
            <div className={styles.noteSection}>Análisis</div>
            <div className={styles.noteLine} style={{ width: '84%' }} />
            <div className={styles.noteSection}>Plan</div>
            <div className={styles.noteLine} style={{ width: '68%' }} />
            <div className={`${styles.noteLine} ${styles.noteTyping}`} style={{ width: '44%' }} />
          </div>

          <ul className={styles.features}>
            <li>
              <span className={styles.featureIcon}><Video size={16} /></span>
              Video seguro con cifrado de extremo a extremo
            </li>
            <li>
              <span className={styles.featureIcon}><Mic size={16} /></span>
              Triage inteligente por voz antes de entrar a la sala
            </li>
            <li>
              <span className={styles.featureIcon}><FileText size={16} /></span>
              Nota SOAPE y resumen del paciente listos al colgar
            </li>
          </ul>

          <div className={styles.statsRow}>
            <span className={styles.statItem}>
              <span className={styles.statNum}>24/7</span>
              <span className={styles.statLabel}>atención continua</span>
            </span>
            <span className={styles.statItem}>
              <span className={styles.statNum}>E2E</span>
              <span className={styles.statLabel}>cifrado clínico</span>
            </span>
            <span className={styles.statItem}>
              <span className={styles.statNum}>Live</span>
              <span className={styles.statLabel}>sincronización</span>
            </span>
          </div>
        </div>
      </aside>
    </div>
  );
}
